import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  EnvelopeSimpleIcon as Envelope,
  TrashIcon as Trash,
  UserPlusIcon as UserPlus,
  CircleNotchIcon as Spinner,
  CrownIcon as Crown,
  PencilSimpleIcon as Pencil,
  EyeIcon as Eye,
  ClipboardIcon as Clipboard,
  CheckCircleIcon as CheckCircle,
} from "@phosphor-icons/react";
import {
  listWorkspaceMembers,
  inviteWorkspaceMember,
  updateWorkspaceMemberRole,
  removeWorkspaceMember,
  revokeWorkspaceInvite,
} from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const ROLE_OPTIONS = [
  { id: "editor", label: "Editor", desc: "Can create + edit sessions and deliverables", Icon: Pencil },
  { id: "viewer", label: "Viewer", desc: "Read-only access", Icon: Eye },
];

const ROLE_BADGE = {
  owner: { label: "Owner", Icon: Crown, color: "#0f62fe" },
  editor: { label: "Editor", Icon: Pencil, color: "#6929c4" },
  viewer: { label: "Viewer", Icon: Eye, color: "#525252" },
};

export default function MembersTab({ workspaceId, isOwner, currentUserId }) {
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviting, setInviting] = useState(false);
  const [devAcceptUrl, setDevAcceptUrl] = useState(null);
  const [devLinkCopied, setDevLinkCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await listWorkspaceMembers(workspaceId);
      setMembers(data.members || []);
      setPending(data.pending || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setDevAcceptUrl(null);
    try {
      const res = await inviteWorkspaceMember(workspaceId, inviteEmail.trim().toLowerCase(), inviteRole);
      if (res.kind === "added") {
        toast.success(`${res.member.email} added as ${res.member.role}.`);
      } else if (res.email_sent) {
        toast.success(`Invitation email sent to ${res.invite.email}.`);
      } else {
        toast.message(`Invite created for ${res.invite.email}.`, {
          description: "Email sending is in stub mode — share the link manually below.",
        });
        if (res.accept_url_for_dev) setDevAcceptUrl(res.accept_url_for_dev);
      }
      setInviteEmail("");
      setInviteRole("editor");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to invite member");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await updateWorkspaceMemberRole(workspaceId, userId, role);
      toast.success(`Role updated to ${role}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update role");
    }
  };

  const handleRemove = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from this workspace?`)) return;
    try {
      await removeWorkspaceMember(workspaceId, userId);
      toast.success(`${email} removed.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to remove member");
    }
  };

  const handleRevokeInvite = async (email) => {
    if (!window.confirm(`Revoke pending invite for ${email}?`)) return;
    try {
      await revokeWorkspaceInvite(workspaceId, email);
      toast.success(`Invite for ${email} revoked.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to revoke invite");
    }
  };

  const copyDevLink = () => {
    if (!devAcceptUrl) return;
    navigator.clipboard.writeText(devAcceptUrl).catch(() => {});
    setDevLinkCopied(true);
    setTimeout(() => setDevLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="ml-card p-10 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="members-loading">
        <Spinner size={16} className="animate-spin inline mr-2" />
        Loading members…
      </div>
    );
  }

  return (
    <div data-testid="workspace-members-tab" className="space-y-6">
      {isOwner && (
        <form onSubmit={handleInvite} className="ml-card p-5" data-testid="invite-form">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-3 flex items-center gap-1.5">
            <UserPlus size={11} weight="bold" />
            Invite a teammate
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="email"
              placeholder="teammate@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              data-testid="invite-email"
              className="flex-1 px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              data-testid="invite-role"
              className="px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm bg-white"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              data-testid="invite-submit"
              className="px-4 py-2 bg-[var(--ml-primary)] text-white text-sm font-semibold hover:bg-[var(--ml-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
            >
              {inviting ? <Spinner size={14} className="animate-spin" /> : <Envelope size={14} weight="bold" />}
              {inviting ? "Sending…" : "Send invite"}
            </button>
          </div>
          <p className="text-xs text-[var(--ml-text-secondary)] mt-2">
            If the email already has an account, they&apos;ll be added immediately. Otherwise we&apos;ll send an invitation email valid for 7 days.
          </p>
          {devAcceptUrl && (
            <div className="mt-3 p-3 border border-dashed border-[var(--ml-border)] bg-[var(--ml-surface)]" data-testid="dev-accept-url-banner">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">
                Dev mode — share this link manually
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs flex-1 break-all" data-testid="dev-accept-url">{devAcceptUrl}</code>
                <button
                  type="button"
                  onClick={copyDevLink}
                  data-testid="copy-dev-accept-url-btn"
                  className="ml-btn-ghost text-xs flex items-center gap-1"
                >
                  {devLinkCopied ? <CheckCircle size={12} weight="bold" /> : <Clipboard size={12} weight="bold" />}
                  {devLinkCopied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </form>
      )}

      {/* Active members */}
      <div className="ml-card divide-y divide-[var(--ml-border)]" data-testid="members-list">
        <div className="px-5 py-3 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center justify-between">
          <span>Active members ({members.length})</span>
        </div>
        {members.map((m) => {
          const badge = ROLE_BADGE[m.role] || ROLE_BADGE.viewer;
          const BadgeIcon = badge.Icon;
          const isMe = m.user_id === currentUserId;
          return (
            <div
              key={m.user_id}
              data-testid={`member-row-${m.user_id}`}
              className="px-5 py-3 flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {m.name}
                  {isMe && <span className="ml-2 text-xs text-[var(--ml-text-secondary)]">(you)</span>}
                </div>
                <div className="text-xs text-[var(--ml-text-secondary)] truncate">{m.email}</div>
              </div>
              <div className="flex items-center gap-2">
                {isOwner && m.role !== "owner" && !isMe ? (
                  <select
                    value={m.role}
                    onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                    data-testid={`role-select-${m.user_id}`}
                    className="text-xs px-2 py-1 border border-[var(--ml-border)] bg-white focus:outline-none focus:border-[var(--ml-primary)]"
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                ) : (
                  <div
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border"
                    style={{ borderColor: badge.color, color: badge.color }}
                    data-testid={`role-badge-${m.user_id}`}
                  >
                    <BadgeIcon size={11} weight="bold" />
                    {badge.label}
                  </div>
                )}
                {isOwner && m.role !== "owner" && !isMe && (
                  <button
                    onClick={() => handleRemove(m.user_id, m.email)}
                    data-testid={`remove-member-${m.user_id}`}
                    title="Remove from workspace"
                    className="p-1.5 text-[var(--ml-text-secondary)] hover:text-[#a2191f] hover:bg-[#fff1f1] transition-colors"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pending invites */}
      {pending.length > 0 && (
        <div className="ml-card divide-y divide-[var(--ml-border)]" data-testid="pending-invites-list">
          <div className="px-5 py-3 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Pending invites ({pending.length})
          </div>
          {pending.map((p) => (
            <div
              key={p.email}
              data-testid={`pending-row-${p.email}`}
              className="px-5 py-3 flex items-center gap-4"
            >
              <Envelope size={14} className="text-[var(--ml-text-secondary)] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{p.email}</div>
                <div className="text-xs text-[var(--ml-text-secondary)]">
                  Invited as {p.role} · waiting to accept
                </div>
              </div>
              {isOwner && (
                <button
                  onClick={() => handleRevokeInvite(p.email)}
                  data-testid={`revoke-invite-${p.email}`}
                  title="Revoke this invite"
                  className="p-1.5 text-[var(--ml-text-secondary)] hover:text-[#a2191f] hover:bg-[#fff1f1] transition-colors"
                >
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
